import {useMemo,useState,useEffect} from 'react'
import {useFilteredMetricProgressData} from './hooks'
import { Line } from 'react-chartjs-2'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import {useMetricProgressLogger} from './hooks'
import {dateToYMD} from '../../util/datetime'
import { useDebounceCallback } from '@react-hook/debounce'
import LocaleDate from '../LocaleDate/LocaleDate'
import DataInput from './DataInput'
import {secondsToTimeObj} from '../../util/datetime'
import {ellipsis} from '../../util/util'
import css from './Metrics.module.css'

const ChartOverlay = ({stat,onHide,metric})=>{
	const [localValue,setLocalValue] = useState(stat.value)
	const [localNotes,setLocalNotes] = useState(stat.notes)
	const [localDate,setLocalDate] = useState(stat.date)

	const {statusLog} = useMetricProgressLogger()

	useEffect(()=>{
		setLocalValue(stat.value)
		setLocalNotes(stat.notes)
	},[stat])

	const debouncedLog = useDebounceCallback(statusLog,1000)

	const handleValueChange = value => {
		setLocalValue(value)
		const payload = {
			overwrite_existing:true,
			metric_id:stat.metric_id,
			date:localDate,
			notes:localNotes,
			value
		}
		//time units require special handling
		if (stat.unit_id === 2) {
			const timeObj = secondsToTimeObj(value)
			payload.hours = timeObj.hours
			payload.minutes = timeObj.minutes
			payload.seconds = timeObj.seconds
		} else {
			payload.value = value
		}
		debouncedLog(payload)
	}

	const handleNotesChange = e => {
		const notes = e.target.value
		setLocalNotes(notes)
		const payload = {
			overwrite_existing:true,
			metric_id:stat.metric_id,
			date:localDate,
			value:localValue,
			notes
		}
		//time units require special handling
		if (stat.unit_id === 2) {
			const timeObj = secondsToTimeObj(localValue)
			payload.hours = timeObj.hours
			payload.minutes = timeObj.minutes
			payload.seconds = timeObj.seconds
		} else {
			payload.value = localValue
		}
		debouncedLog(payload)
	}		
	return (
		<div onClick={onHide} style={{
			position:'absolute',
			top:0,left:0,
			width:'100%',
			height:'100%',
			background:'rgba(0,0,0,0.4)',
			borderRadius:'7px',
			display:'flex',
			alignItems:'center',
			justifyContent:'center',
			zIndex:9000
		}}>
			<div 
				onClick={e=>{
					e.stopPropagation()
				}}
				style={{width:'360px',maxWidth:'90%',borderRadius:'7px',border:'1px solid #ccc'}}
				className="bg-white p-2"
			>
				<div className="d-flex mb-1 align-items-start">
					<div>
						<small className="d-block">
							<span>{metric.assessment_name}</span>
							<span className="mx-1">-</span>
							<LocaleDate 
								date={new Date(`${stat.date}T00:00:00`)}
								includeTime={false}
							/>
						</small>
						<small className="d-block text-muted">Edits auto saved</small>
					</div>
					<Button 
						className="ml-auto"
						onClick={onHide}
						variant="outline-danger"
					>
						<i className="fa fa-times"/>
					</Button>
				</div>
				<DataInput 
					onChange={handleValueChange}
					value={localValue}
					unit={stat.unit_id}
				/>
				<Form.Control 
					onChange={handleNotesChange}
					className="mt-2"
					value={localNotes}
					placeholder="notes"
					as="textarea" 
				/>
			</div>
		</div>
	)
}

const ProgressGraph = ({id})=>{
	const data = useFilteredMetricProgressData(id)
	//console.log(data)
	const {stats} = data
	const labels = stats.map( d => d.date )
	const chartData = stats.map( d => ({y:d.value,x:d.date,notes:d.notes}) )
	const r = ~~(100 * Math.random() - 25)
	//memoize the colors
	const borderColor = useMemo(()=>`hsla(${213-r}, 100%, 60%,0.8)`,[])
	const backgroundColor = useMemo(()=>`hsla(${213-r}, 100%, 60%,0.4)`,[])

	const [showOverlay,setShowOverlay] = useState(false)
	const [overlayStat,setOverlayStat] = useState(null)

	if ( stats.length === 0 ) return null
	return (
		<div className="w-100">
			<div style={{height:'180px',width:'100%',position:'relative'}}>
				<Line
					options={{
						title:{
							display:true,
							text:data.assessment_name,
							fontColor:'#1f1f1f',
							fontSize:18
						},
						tooltips:{
							callbacks:{
								afterLabel:({index})=>{
									const {notes} = stats[index]
									return (
										Boolean(notes)
										? `----------------\nNotes: ${ellipsis(notes,50)}`
										: ''
									)
								}
							}
						},
						onClick: function(evt, element) {
							if(element.length > 0){
								const index = element[0]._index
								const stat = stats[index]
								setOverlayStat(stat)
								setShowOverlay(true)
							}
						},
						legend:{
							display:false
						},
						animation: {
							duration: 0
						},
						maintainAspectRatio:false,
						responsive:true,
						scales:{
							xAxes:[{
								ticks:{
									//create local date labels
									callback:(value)=>new Date(`${value}T00:00:00`).toLocaleString([],{ year: 'numeric',month: '2-digit',day: 'numeric',})
								}
							}],
							yAxes:[{
								scaleLabel:{
									display:data.unit!=='None',
									labelString:data.unit==='time'?'seconds':data.unit,
									fontSize:14
								}
							}]
						}
					}}
					data={
						{
							labels,
							datasets: [{
								lineTension: 0,
								label: data.assessment_name,
								backgroundColor,
								borderColor,
								data:chartData,
								pointRadius: ({dataIndex,dataset:{data}})=>{
									const {notes} = data[dataIndex]
									return (
										Boolean(notes)
										? 4
										: 3
									)
								},
								pointHoverRadius: ({dataIndex,dataset:{data}})=>{
									const {notes} = data[dataIndex]
									return (
										Boolean(notes)
										? 6
										: 5
									)
								},
								pointBackgroundColor: ({dataIndex,dataset:{data}})=>{
									const {notes} = data[dataIndex]
									return (
										Boolean(notes)
										? '#fff'
										: backgroundColor
									)
								},
								pointBorderColor: ({dataIndex,dataset:{data}})=>{
									const {notes} = data[dataIndex]
									return (
										Boolean(notes)
										? '#1f1f1f'
										: borderColor
									)
								}
							}]
						}
					}
				/>
				{showOverlay&&
					<ChartOverlay 
						metric={data}
						stat={overlayStat} 
						onHide={()=>{
							setShowOverlay(false)
						}}
					/>
				}
			</div>
		</div>
	)
}

export default ProgressGraph